import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { mutate } from 'swr';
import { v4 as uuidv4 } from 'uuid';
import { Button } from 'src/components/Buttons/Button';
import { useNotification } from 'src/components/Notification/useNotfication';
import container from 'src/container';
import {
    Organization,
    OrganizationDocument,
    OrganizationDocumentViewCount,
} from 'src/domain/organization';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import { OrganizationDocumentPreviewModal } from './OrganizationDocumentPreviewModal';

const {
    cradle: { munduClient, storageService },
} = container;

const MAX_DOCUMENT_SIZE_BYTES = 10 * 1024 * 1024;

const SUPPORTED_DOCUMENT_MIME_TYPES = new Set([
    'application/msword',
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

const isSupportedDocument = (file: File) =>
    file.type.startsWith('image/') ||
    SUPPORTED_DOCUMENT_MIME_TYPES.has(file.type);

const isWithinDocumentSizeLimit = (file: File) =>
    file.size <= MAX_DOCUMENT_SIZE_BYTES;

type DocumentListItem = {
    localId: string;
    document: OrganizationDocument;
    file?: File;
};

type Props = {
    organization: Organization;
};

const createExistingItem = (
    document: OrganizationDocument,
): DocumentListItem => ({
    localId: document.id,
    document: {
        ...document,
        displayName: document.displayName ?? '',
    },
});

const createItemsFromOrganization = (
    organization: Organization,
): DocumentListItem[] => (organization.documents ?? []).map(createExistingItem);

const serializeItems = (items: DocumentListItem[]) =>
    JSON.stringify(
        items.map(item => ({
            blobName: item.document.blobName,
            createdDate: item.document.createdDate,
            displayName: item.document.displayName ?? '',
            id: item.document.id,
            mimeType: item.document.mimeType,
            originalName: item.document.originalName,
            size: item.document.size ?? null,
            uploadedById: item.document.uploadedById,
        })),
    );

export const OrganizationDocumentsSection = ({ organization }: Props) => {
    const { t } = useTranslation();
    const notify = useNotification();
    const user = useSignedInUser();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [folderName, setFolderName] = useState(
        organization.documentsFolderName ?? '',
    );
    const [items, setItems] = useState<DocumentListItem[]>(
        createItemsFromOrganization(organization),
    );
    const [isSaving, setIsSaving] = useState(false);
    const [previewItem, setPreviewItem] = useState<DocumentListItem | null>(
        null,
    );
    const { data: viewCounts } = useSWR<OrganizationDocumentViewCount[]>(
        ['organization-document-view-counts', organization.id],
        (_key, id) => munduClient.listOrganizationDocumentViewCounts(id),
    );
    const viewCountsByDocumentId = useMemo(
        () =>
            new Map(
                (viewCounts ?? []).map(
                    count => [count.documentId, count] as const,
                ),
            ),
        [viewCounts],
    );

    useEffect(() => {
        setPreviewItem(null);
        setFolderName(organization.documentsFolderName ?? '');
        setItems(createItemsFromOrganization(organization));
    }, [organization]);

    const initialFolderName = organization.documentsFolderName ?? '';
    const initialItemsSerialized = useMemo(
        () => serializeItems(createItemsFromOrganization(organization)),
        [organization],
    );
    const currentItemsSerialized = useMemo(
        () => serializeItems(items),
        [items],
    );
    const isDirty =
        folderName !== initialFolderName ||
        currentItemsSerialized !== initialItemsSerialized;

    const handleFileSelection = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(event.target.files ?? []);

        if (!selectedFiles.length) {
            return;
        }

        const supportedFiles = selectedFiles.filter(isSupportedDocument);

        if (supportedFiles.length !== selectedFiles.length) {
            notify(t('admin.documents.unsupportedType'), true);
        }

        const validFiles = supportedFiles.filter(isWithinDocumentSizeLimit);

        if (validFiles.length !== supportedFiles.length) {
            notify(t('admin.documents.fileTooLarge'), true);
        }

        if (!validFiles.length) {
            if (event.target) {
                event.target.value = '';
            }
            return;
        }

        const nextItems = validFiles.map(file => {
            const documentId = uuidv4();

            return {
                localId: documentId,
                file,
                document: {
                    id: documentId,
                    blobName: '',
                    originalName: file.name,
                    displayName: '',
                    mimeType: file.type,
                    size: file.size,
                    uploadedById: user?.id ?? organization.id,
                    createdDate: new Date().toISOString(),
                },
            };
        });

        setItems(current => [...current, ...nextItems]);

        if (event.target) {
            event.target.value = '';
        }
    };

    const handleDisplayNameChange = (localId: string, displayName: string) => {
        setItems(current =>
            current.map(item =>
                item.localId === localId
                    ? {
                          ...item,
                          document: {
                              ...item.document,
                              displayName,
                          },
                      }
                    : item,
            ),
        );
    };

    const handleRemove = (localId: string) => {
        setItems(current => current.filter(item => item.localId !== localId));
    };

    const handleSave = async () => {
        const trimmedFolderName = folderName.trim();
        const uploadedBlobNames: string[] = [];

        if (items.length > 0 && !trimmedFolderName) {
            notify(t('admin.documents.folderNameRequired'), true);
            return;
        }

        setIsSaving(true);

        try {
            let sas = undefined;
            const nextDocuments: OrganizationDocument[] = [];

            for (const item of items) {
                const baseDocument = {
                    ...item.document,
                    displayName: item.document.displayName?.trim() || undefined,
                };

                if (item.file) {
                    if (!sas) {
                        sas = await munduClient.getOrganizationDocumentsSas(
                            organization.id,
                        );
                    }

                    const blobName = `${uuidv4()}-${item.file.name}`;
                    await storageService.uploadImage(item.file, blobName, sas);
                    uploadedBlobNames.push(blobName);

                    nextDocuments.push({
                        ...baseDocument,
                        blobName,
                    });
                    continue;
                }

                nextDocuments.push(baseDocument);
            }

            const updatedOrganization =
                await munduClient.updateOrganizationDocuments(organization.id, {
                    documentsFolderName: trimmedFolderName || undefined,
                    documents: nextDocuments,
                });

            setFolderName(updatedOrganization.documentsFolderName ?? '');
            setItems(createItemsFromOrganization(updatedOrganization));
            notify(t('admin.notifications.organizationDocumentsUpdated'));
            mutate(['orginfo', organization.id], updatedOrganization, false);
            mutate(
                ['organization', organization.id],
                updatedOrganization,
                false,
            );
        } catch (error) {
            if (uploadedBlobNames.length > 0) {
                try {
                    const rollbackSas =
                        await munduClient.getOrganizationDocumentsSas(
                            organization.id,
                        );
                    await Promise.all(
                        uploadedBlobNames.map(blobName =>
                            storageService.deleteImage(blobName, rollbackSas),
                        ),
                    );
                } catch (_rollbackError) {}
            }

            if (error instanceof Error) {
                notify(error.message, true);
            } else {
                notify(t('admin.notifications.genericError'), true);
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
                <h2 className="text-xl font-semibold text-woodyBrown">
                    {t('admin.documents.title')}
                </h2>
                <p className="text-sm text-mongooseGray">
                    {t('admin.documents.description')}
                </p>
            </div>

            <div className="grid grid-cols-1 gap-3">
                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-woodyBrown">
                        {t('admin.documents.folderNameLabel')}
                    </label>
                    <input
                        className="text-input"
                        type="text"
                        value={folderName}
                        onChange={event => setFolderName(event.target.value)}
                        placeholder={t('admin.documents.folderNamePlaceholder')}
                    />
                </div>

                <div className="flex items-center gap-3">
                    <Button
                        title={t('admin.documents.uploadButton')}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isSaving}
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="application/pdf,application/msword,application/vnd.ms-excel,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.presentationml.presentation,image/*"
                        className="hidden"
                        onChange={handleFileSelection}
                    />
                </div>
            </div>

            {items.length === 0 ? (
                <p className="text-sm text-mongooseGray">
                    {t('admin.documents.empty')}
                </p>
            ) : (
                <div className="flex flex-col gap-3">
                    {items.map(item => (
                        <div
                            key={item.localId}
                            className="rounded-lg border border-woodyBrown/20 bg-white p-4"
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="min-w-0 flex-1">
                                    <p className="truncate font-medium text-woodyBrown">
                                        {item.document.originalName}
                                    </p>
                                    <p className="mt-1 text-xs text-mongooseGray">
                                        {t('admin.documents.originalName')}:{' '}
                                        {item.document.originalName}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="text-sm text-woodyBrown underline"
                                    aria-label={t(
                                        'admin.documents.previewLabel',
                                        {
                                            name:
                                                item.document.displayName?.trim() ||
                                                item.document.originalName,
                                        },
                                    )}
                                    onClick={() => setPreviewItem(item)}
                                >
                                    {t('admin.documents.previewButton')}
                                </button>
                                <button
                                    type="button"
                                    className="text-sm text-venetianRed underline"
                                    onClick={() => handleRemove(item.localId)}
                                    disabled={isSaving}
                                >
                                    {t('admin.documents.removeButton')}
                                </button>
                            </div>

                            <div className="mt-3 flex flex-col gap-1">
                                <label className="text-sm font-medium text-woodyBrown">
                                    {t('admin.documents.displayName')}
                                </label>
                                <input
                                    className="text-input"
                                    type="text"
                                    value={item.document.displayName ?? ''}
                                    onChange={event =>
                                        handleDisplayNameChange(
                                            item.localId,
                                            event.target.value,
                                        )
                                    }
                                    placeholder={t(
                                        'admin.documents.displayNamePlaceholder',
                                    )}
                                />
                            </div>
                            {!item.file && viewCounts && (
                                <p className="mt-3 text-xs text-mongooseGray">
                                    {t('admin.documents.openedBy')}:{' '}
                                    {t('admin.documents.residentViews', {
                                        count:
                                            viewCountsByDocumentId.get(
                                                item.document.id,
                                            )?.residentCount ?? 0,
                                    })}
                                    {', '}
                                    {t('admin.documents.relativeViews', {
                                        count:
                                            viewCountsByDocumentId.get(
                                                item.document.id,
                                            )?.relativeCount ?? 0,
                                    })}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {previewItem && (
                <OrganizationDocumentPreviewModal
                    organizationId={organization.id}
                    item={previewItem}
                    onClose={() => setPreviewItem(null)}
                />
            )}
            <div className="inline-flex justify-end">
                <Button
                    title={t('admin.documents.saveButton')}
                    onClick={handleSave}
                    disabled={!isDirty || isSaving}
                    active={isDirty && !isSaving}
                />
            </div>
        </div>
    );
};
